import {
  formatToCurrency,
  roundNumber,
  formatCurrencyOrNumber
} from "src/utils/genericFunctions";
import { ManualTransferInitialState, POItem } from "../interface";
import { v4 as uuidv4 } from "uuid";
import { getFormattedDate } from "src/utils/getDates";
import { defaultDateFormat } from "src/config/constants/genericConstants";
import stringToNumber from "src/utils/stringToNumber";
import { NUMERIC_INPUT_DEFAULT_MAX } from "src/components/GoTableWithSelect/components/cellComponents/NumericInputWithMax";

export const initialState: ManualTransferInitialState = {
  poItems: [],
  poItemsCopy: [],
  searchQuery: "",
  searchResults: [],
  storeList: [],
  blockNavigation: true,
  supplier: "",
  effectiveDate: "",
  purchasingOrganization: "",
  purchasingOrganizationId: "",
  notes: "",
  createPOLoading: false,
  searchProductsLoading: false,
  margin: 0,
  totalCost: 0,
  transfer: 0,
  attachments: [],
  loading: false,
  status: "",
  invoiceType: "Manual Transfer",
  invoiceLabel: "",
  invoiceNumber: "",
  receivedDate: "",
  createdBy: "",
  initialPOItems: [],
  editData: {},
  deletedAttachmentPaths: [],
  resetSort: false,
  deletedInternalIds: {}
};

export const calculateTotalLanded = (item: any) =>
  ((+item?.receivedEaches || 0) +
    (+item?.receivedCases || 0) * (+item?.packOrSize?.split("/")[0] || 1)) *
  stringToNumber(item?.landed || 0);

export const calculateTotalExtendedRetail = (item: any) =>
  ((+item.receivedEaches || 0) +
    (+item.receivedCases || 0) * +item.packOrSize?.split("/")[0]) *
  stringToNumber(item.retail || 0);

const getTotalEaches = (item: POItem): number => {
  return (
    (+item?.receivedEaches || 0) +
    ((+item.receivedCases || 0) * +item?.packOrSize?.split("/")[0] || 0)
  );
};

export const handleAdditem = (state: any, action: any) => {
  state.resetSort = true;
  const { item, uniqueId: clientUniqueId } = action.payload;
  const uniqueId = clientUniqueId ?? uuidv4();
  const internalId = state.deletedInternalIds[item?.itemId];
  if (!state.poItems) state.poItems = [];
  if (!state.poItemsCopy) state.poItemsCopy = [];
  const awaitingProductPricing = Boolean(clientUniqueId);
  const retailNum = awaitingProductPricing
    ? ""
    : stringToNumber(item?.retail ?? 0);
  let landedNum = awaitingProductPricing
    ? ""
    : stringToNumber(item?.landed ?? 0);
  if (!awaitingProductPricing && landedNum > retailNum) {
    landedNum = retailNum;
  }
  const retailDisplay = retailNum;
  const landedDisplay = landedNum;
  const newData = {
    ...item,
    pack: item.packOrSize?.split("/")[0],
    retail: retailDisplay,
    landed: landedDisplay,
    costInput: landedDisplay,
    retailInput: retailDisplay,
    extendedRetail: "$0.00",
    extendedCost: "$0.00",
    margin: "0",
    uniqueId,
    pricingLoading: awaitingProductPricing,
    inputDisabled: {
      receivedCases: item?.isRandomWeight
    },
    internalId
  };
  state.poItems.unshift(newData);
  state.poItemsCopy.unshift(newData);
};

export const handleRemovePoItem = (
  state: ManualTransferInitialState,
  action: { payload: { selectedPoItems: string[]; storeId: string } }
) => {
  const { selectedPoItems } = action.payload;
  const filterData = (data: any) =>
    data?.filter((row: any) => !selectedPoItems?.includes(row.uniqueId));
  state?.poItems?.forEach((row: any) => {
    if (selectedPoItems.includes(row.uniqueId)) {
      state.deletedInternalIds[row?.itemId] = row.internalId;
    }
  });
  state.poItems = filterData(state?.poItems);
  state.poItemsCopy = filterData(state?.poItemsCopy);
  const { totalLanded, totalExtendedRetail } = updateItemsCasesAndRetail(
    state?.poItems,
    "",
    "",
    ""
  );
  state.totalCost = totalExtendedRetail;
  state.transfer = totalLanded;
  state.margin =
    totalExtendedRetail === 0
      ? 0
      : calculatemargin(totalExtendedRetail, totalLanded);
};

export const filterItemsByCharacters = (data: any, searchQuery: any) =>
  data?.filter((poItem: any) => {
    return (
      poItem?.itemId?.toString().includes(searchQuery) ||
      poItem?.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

export const handleSearchPoItem = (
  state: ManualTransferInitialState,
  action: { payload: { searchQuery: string; storeId: string } }
) => {
  const { searchQuery } = action.payload;
  state.poItems = filterItemsByCharacters(state?.poItemsCopy, searchQuery);
};

export const handleClearSearchResult = (state: ManualTransferInitialState) => {
  state.searchResults = [];
};

export const handleEditReceivedEaches = (
  state: ManualTransferInitialState,
  action: {
    payload: { itemId: string; receivedQty: string; storeId: string };
  }
) => updateEachesCasesConditionally(state, action, "each");

export const handleEditCostAndRetail = (
  state: ManualTransferInitialState,
  action: {
    payload: {
      itemId: string;
      id: string;
      value: string;
    };
  }
) => {
  const { itemId, id, value } = action.payload;

  let totalCost = 0;
  let totalExtendedRetail = 0;
  let totalEaches = 0;

  const updateCostAndRetail = (item: any, id: string, value: string) => {
    if (id === "cost") {
      item.landed = value;
      item.costInput = value;
    } else {
      item.retail = value;
      item.retailInput = value;
    }

    const extendedRetail =
      getTotalEaches(item) * stringToNumber(item.retail || 0);
    const extendedCost = calculateTotalLanded(item);

    item.extendedRetail = formatToCurrency(extendedRetail);
    item.extendedCost = formatToCurrency(extendedCost);
    item.margin =
      extendedRetail === 0 ? 0 : +calculatemargin(extendedRetail, extendedCost);
  };

  const updatedItems = state.poItemsCopy.map((item: any) => {
    if (item.itemId === itemId) {
      updateCostAndRetail(item, id, value);
    }
    totalCost += calculateTotalLanded(item);
    totalExtendedRetail += calculateTotalExtendedRetail(item);
    totalEaches += getTotalEaches(item);
    return item;
  });

  state.poItemsCopy = updatedItems;
  state.totalCost = totalExtendedRetail;
  state.transfer = totalCost;
  state.margin =
    totalEaches === 0 || totalExtendedRetail === 0
      ? 0
      : +calculatemargin(totalExtendedRetail, totalCost);

  state.poItems = state.poItems.map((item: any) => {
    if (item.itemId === itemId) {
      updateCostAndRetail(item, id, value);
    }
    return item;
  });
};

const recalculateManualTransferTotals = (state: ManualTransferInitialState) => {
  let totalCost = 0;
  let totalExtendedRetail = 0;
  let totalEaches = 0;
  state.poItemsCopy.forEach((item: any) => {
    totalCost += calculateTotalLanded(item);
    totalExtendedRetail += calculateTotalExtendedRetail(item);
    totalEaches += getTotalEaches(item);
  });
  state.totalCost = totalExtendedRetail;
  state.transfer = totalCost;
  state.margin =
    totalEaches === 0 || totalExtendedRetail === 0
      ? 0
      : +calculatemargin(totalExtendedRetail, totalCost);
};

export const handleFulfillProductPricing = (
  state: ManualTransferInitialState,
  action: {
    payload: { uniqueId: string; retail?: string; cost?: string };
  }
) => {
  const { uniqueId, retail, cost } = action.payload;

  const patchItem = (item: any) => {
    if (item.uniqueId !== uniqueId) {
      return item;
    }

    const retailNum = stringToNumber(retail ?? item.retail ?? 0);

    // We check if the cost is 0, if it is, we set the cost to the landed price
    let landedNum = stringToNumber(cost ?? null);
    if (landedNum === 0) {
      landedNum = stringToNumber(item.landed ?? 0);
    }

    const next = {
      ...item,
      retail: retailNum,
      landed: landedNum,
      costLimit: landedNum === 0 ? NUMERIC_INPUT_DEFAULT_MAX : landedNum,
      costInput: landedNum,
      retailInput: retailNum,
      pricingLoading: false
    };

    const extendedRetailNum =
      getTotalEaches(next) * stringToNumber(next.retail || 0);
    const extendedCostNum = calculateTotalLanded(next);
    next.extendedRetail = formatToCurrency(extendedRetailNum);
    next.extendedCost = formatToCurrency(extendedCostNum);
    next.margin =
      extendedRetailNum === 0
        ? 0
        : +calculatemargin(extendedRetailNum, extendedCostNum);

    return next;
  };

  state.poItems = state.poItems.map(patchItem);
  state.poItemsCopy = state.poItemsCopy.map(patchItem);
  recalculateManualTransferTotals(state);
};

export const handleRejectProductPricing = (
  state: ManualTransferInitialState,
  action: any
) => {
  const uniqueId = action.meta?.arg?.uniqueId;
  if (!uniqueId) {
    return;
  }
  const clearLoading = (item: any) =>
    item.uniqueId === uniqueId ? { ...item, pricingLoading: false } : item;
  state.poItems = state.poItems.map(clearLoading);
  state.poItemsCopy = state.poItemsCopy.map(clearLoading);
};

const updateItemsCasesAndRetail = (
  data: any,
  itemId = "",
  receivedQty = "",
  type = ""
) => {
  let totalEaches: any = 0;
  let totalLanded: any = 0;
  let totalExtendedRetail: any = 0;
  const result = data.map((item: any) => {
    if (item.itemId === itemId) {
      if (type === "case") {
        item.receivedCases = receivedQty;
      } else {
        item.receivedEaches = receivedQty;
      }
      const extendedRetail =
        getTotalEaches(item) * stringToNumber(item.retail || 0);
      const extendedCost = calculateTotalLanded(item);

      item.extendedRetail = formatToCurrency(extendedRetail);
      item.extendedCost = formatToCurrency(extendedCost);
      item.margin =
        extendedRetail === 0
          ? 0
          : +calculatemargin(extendedRetail, extendedCost);
    }
    totalLanded += calculateTotalLanded(item);
    totalExtendedRetail += calculateTotalExtendedRetail(item);
    totalEaches += getTotalEaches(item);
    return item;
  });
  return { totalEaches, result, totalLanded, totalExtendedRetail };
};

export const updateEachesCasesConditionally = (
  state: any,
  action: any,
  type: any
) => {
  const { itemId, receivedQty } = action.payload;
  const {
    result: copyItemResult,
    totalLanded,
    totalExtendedRetail
  } = updateItemsCasesAndRetail(state.poItemsCopy, itemId, receivedQty, type);
  const { result: itemResult, totalEaches } = updateItemsCasesAndRetail(
    state.poItems,
    itemId,
    receivedQty,
    type
  );
  state.poItemsCopy = copyItemResult;
  state.poItems = itemResult;
  state.totalCost = totalExtendedRetail;
  state.transfer = totalLanded;
  state.margin =
    totalEaches === 0 || totalExtendedRetail === 0
      ? 0
      : +calculatemargin(totalExtendedRetail, totalLanded);
};

export const handleEditReceivedCases = (
  state: ManualTransferInitialState,
  action: {
    payload: { itemId: string; receivedQty: string; storeId: string };
  }
) => updateEachesCasesConditionally(state, action, "case");

export const handleReset = () => initialState;

export const handleUpdateNavigationBlocking = (
  state: ManualTransferInitialState,
  action: any
) => {
  state.blockNavigation = action.payload;
};

export const resetPoitemsCopy = (data: any) =>
  data?.map((poItem: any) => ({
    ...poItem,
    receivedCases: "0",
    receivedEaches: "0",
    extendedRetail: "0"
  }));

export const handleUpdateReceivingStore = (
  state: ManualTransferInitialState,
  action: any
) => {
  const store = state.storeList?.find((store) => store.id === action.payload);
  state.purchasingOrganizationId = action.payload;
  state.purchasingOrganization = store?.name;
};

export const handleUpdateCreatedBy = (
  state: ManualTransferInitialState,
  action: any
) => {
  state.createdBy = action.payload;
};

export const handleUpdateNotes = (
  state: ManualTransferInitialState,
  action: any
) => {
  const notes = action.payload;
  state.notes = notes;
};

export const handleAddAttachment = (
  state: ManualTransferInitialState,
  action: {
    payload: { fileName: string; fileURL: string; fileType: string };
  }
) => {
  state.attachments.push(action.payload);
};

export const handleEnableSort = (
  state: ManualTransferInitialState,
  action: any
) => {
  state.resetSort = action.payload;
};

export const handleRemoveAttachment = (
  state: ManualTransferInitialState,
  action: { payload: number }
) => {
  const filterAttachments = state.attachments.filter(
    (_item: any, itemIndex: number) => itemIndex !== action?.payload
  );

  state.deletedAttachmentPaths = [
    ...state.deletedAttachmentPaths,
    state.attachments[action.payload].fileURL
  ];

  state.attachments = filterAttachments;
};

export const handlePendingManualTransferTransferPo = (state: any) => {
  state.createPOLoading = true;
};

export const handleFulfillManualTransferTransferPo = (state: any) => {
  state.createPOLoading = false;
};

export const handleRejectManualTransferTransferPo = (state: any) => {
  state.createPOLoading = false;
};

export const handlePendingSearchProductForMt = (state: any, payload: any) => {
  state.searchProductsLoading = true;
  state.searchQuery = payload.meta.arg.searchQuery;
};

export const formatSearchResutsResponse = (data: any) =>
  data?.map((item: any) => ({
    ...item,
    expiry: item?.expiry
      ? getFormattedDate(new Date(item.expiry), defaultDateFormat)
      : "",
    itemId: item?.itemId?.toString()?.replace(/^0+/, ""),
    retail: formatCurrencyOrNumber(item.retail),
    landed: roundNumber(item.landed)
  }));

export const handleFulfillSearchProductForMt = (state: any, action: any) => {
  const { payload } = action;
  state.searchResults = payload?.data?.length
    ? formatSearchResutsResponse(payload.data)
    : [];
  state.searchProductsLoading = false;
};

export const handleRejectSearchProductForMt = (state: any) => {
  state.searchResults = null;
  state.searchProductsLoading = false;
};

export const handlePendingFetchStoreListForMT = (state: any) => {
  state.createPOLoading = true;
};

export const formatStoreList = (data: any) =>
  data.map((eachStore: any) => ({
    ...eachStore,
    id: `${eachStore.storeId}`.padStart(4, "0"),
    name: eachStore.storeName.split(",")?.[0]
  }));

export const handleFulfillFetchStoreListForMT = (state: any, action: any) => {
  const { payload } = action;
  state.storeList = payload?.data?.length ? formatStoreList(payload.data) : [];
  state.createPOLoading = false;
};

export const handleRejectFetchStoreListForMT = (state: any) => {
  state.storeList = null;
  state.createPOLoading = false;
};

export const handlePendingFetchTransferDetails = (state: any) => {
  state.loading = true;
};

const calculateExtendedRetailForFetchTransferPoItem = (item: any) => {
  let result: any = formatToCurrency(+item.adjustedExtendedRetail);
  if (result) return result;
  result = calculateTotalExtendedRetail(item);
  return formatCurrencyOrNumber(`$${result}`);
};

const formatPoItemsForFetchTransferDetails = (data: any) => {
  let totalLanded = 0;
  let totalExtendedRetail = 0;
  let totalEaches = 0;
  const result = data?.map((item: any) => {
    const updatedItem = {
      ...item,
      expiry: item?.expiry
        ? getFormattedDate(new Date(item.expiry), defaultDateFormat)
        : "",
      extendedRetail: calculateExtendedRetailForFetchTransferPoItem(item),
      uniqueId: uuidv4(),
      inputDisabled: {
        receivedCases: item?.isRandomWeight
      },
      retail: formatCurrencyOrNumber(item?.retail),
      landed: stringToNumber(item.landed)
        ? formatCurrencyOrNumber(item?.landed)
        : "$0",
      extendedCost: item.landed
        ? formatToCurrency(calculateTotalLanded(item))
        : "$0",
      receivedCases: Math.abs(item?.receivedCases),
      receivedEaches: Math.abs(item?.receivedEaches)
    };
    totalLanded += calculateTotalLanded(updatedItem);
    totalExtendedRetail += calculateTotalExtendedRetail(updatedItem);
    totalEaches += getTotalEaches(updatedItem);
    return updatedItem;
  });
  return {
    result,
    totalLanded: roundNumber(totalLanded),
    totalExtendedRetail: roundNumber(totalExtendedRetail),
    totalEaches: roundNumber(totalEaches)
  };
};

export const calculatemargin = (retail: any, trnsfer: any) => {
  const result = ((retail - trnsfer) / retail) * 100;
  return roundNumber(result);
};

export const handleFulFillFetchTransferDetails = (state: any, action: any) => {
  const { payload } = action;
  state.loading = false;
  const data = payload.data;
  const {
    result: items,
    totalLanded,
    totalExtendedRetail,
    totalEaches
  } = formatPoItemsForFetchTransferDetails(data?.poItems);
  state.editData = { ...data, poItems: items };
  state.poItems = items;
  state.poItemsCopy = items;
  state.initialPOItems = items;
  state.notes = data.notes;
  state.totalCost = +totalExtendedRetail;
  state.transfer = +totalLanded;
  state.margin =
    totalEaches === 0 || totalExtendedRetail === 0
      ? 0
      : calculatemargin(totalExtendedRetail, totalLanded);
  state.attachments = data.attachments ?? [];
  state.status = data.status;
  state.invoiceType = data.invoiceType;
  state.invoiceLabel = data.invoiceLabel;
  state.invoiceNumber = data.docId;
  state.receivedDate = data.receivedDate;
  state.createdBy = data.createdByUser;
  state.effectiveDate = data.effectiveDate;
  state.supplier = data.supplier;
  state.purchasingOrganization = data.purchasingOrganization?.name;
  state.purchasingOrganizationId = data.storeId;
  state.supplierId = data.supplierId;
};

export const handleRejectFetchTransferDetails = (state: any) => {
  state.loading = false;
};
